import {
  Add01Icon,
  Archive03Icon,
  Delete01Icon,
  PencilEdit01Icon,
  Search01Icon,
  UndoIcon,
  Wallet01Icon,
} from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import { createFileRoute } from '@tanstack/react-router';

import React, { useState } from 'react';

import { useMutation, useQuery } from 'convex/react';

import { api } from '@/../convex/_generated/api';
import type { AccountId, BalanceSheetId } from '@/../convex/types';
import { AccountModal } from '@/components/AccountModal';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@/components/ui/pagination';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { AuthenticatedRoute } from '@/lib/authenticatedRoute';

function AccountsPage() {
  const { id } = Route.useParams() as { id: string };
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingAccount, setEditingAccount] = useState<AccountId | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [accountToDelete, setAccountToDelete] = useState<AccountId | null>(
    null
  );
  const [showArchived, setShowArchived] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;

  const accounts = useQuery(api.accounts.listForBalanceSheet, {
    balanceSheetId: id as BalanceSheetId,
  });

  const archiveAccount = useMutation(api.accounts.archive);
  const deleteAccount = useMutation(api.accounts.remove);

  const handleCreate = () => {
    setEditingAccount(null);
    setIsModalOpen(true);
  };

  const handleEdit = (accountId: AccountId) => {
    setEditingAccount(accountId);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingAccount(null);
  };

  const handleArchive = async (accountId: AccountId) => {
    try {
      await archiveAccount({ id: accountId });
    } catch (error) {
      console.error('Failed to archive account:', error);
    }
  };

  const handleDeleteClick = (accountId: AccountId) => {
    setAccountToDelete(accountId);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!accountToDelete) return;

    try {
      await deleteAccount({ id: accountToDelete });
      setDeleteDialogOpen(false);
      setAccountToDelete(null);
    } catch (error) {
      console.error('Failed to delete account:', error);
      alert('Failed to delete account');
    }
  };

  if (!accounts) {
    return (
      <div className="p-4">
        <div className="space-y-4">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-96" />
        </div>
      </div>
    );
  }

  const filteredAccounts = accounts
    .filter(account => (showArchived ? true : !account.isArchived))
    .filter(account => {
      if (!searchQuery) return true;
      const query = searchQuery.toLowerCase();
      return (
        account.name.toLowerCase().includes(query) ||
        account.accountType?.label.toLowerCase().includes(query) ||
        account.baseCurrency.toLowerCase().includes(query) ||
        account.side.toLowerCase().includes(query) ||
        account.liquidity.toLowerCase().includes(query) ||
        account.notes?.toLowerCase().includes(query)
      );
    });

  // Reset to page 1 when filters change
  const totalPages = Math.ceil(filteredAccounts.length / itemsPerPage);
  const currentPageNumber = Math.min(currentPage, Math.max(1, totalPages));

  // Get paginated accounts
  const startIndex = (currentPageNumber - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedAccounts = filteredAccounts.slice(startIndex, endIndex);

  return (
    <div className="p-4">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold">Accounts</h1>
          <p className="text-muted-foreground">
            Manage the accounts in your balance sheet
          </p>
        </div>
      </div>

      {filteredAccounts.length === 0 && !searchQuery ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <HugeiconsIcon
              icon={Wallet01Icon}
              size={48}
              strokeWidth={2}
              className="text-muted-foreground mb-4"
            />
            <h3 className="text-lg font-semibold mb-2">
              {showArchived ? 'No archived accounts' : 'No accounts yet'}
            </h3>
            <p className="text-muted-foreground text-center mb-4">
              {showArchived
                ? 'You have no archived accounts.'
                : 'Create your first account to start tracking your balances.'}
            </p>
            {!showArchived && (
              <Button onClick={handleCreate}>
                <HugeiconsIcon
                  icon={Add01Icon}
                  size={16}
                  strokeWidth={2}
                  className="mr-2"
                />
                Add Account
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardHeader>
            <div className="flex items-center gap-4">
              <CardTitle className="flex-shrink-0">
                {filteredAccounts.length} Account
                {filteredAccounts.length !== 1 ? 's' : ''}
                {filteredAccounts.length > itemsPerPage && (
                  <span className="text-sm font-normal text-muted-foreground ml-2">
                    (showing {startIndex + 1}-
                    {Math.min(endIndex, filteredAccounts.length)})
                  </span>
                )}
              </CardTitle>
              <div className="relative flex-1">
                <HugeiconsIcon
                  icon={Search01Icon}
                  size={16}
                  strokeWidth={2}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                />
                <Input
                  placeholder="Search accounts..."
                  value={searchQuery}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setSearchQuery(e.target.value)
                  }
                  className="pl-9"
                />
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => setShowArchived(!showArchived)}
                >
                  {showArchived ? 'Hide Archived' : 'Show Archived'}
                </Button>
                <Button onClick={handleCreate}>
                  <HugeiconsIcon
                    icon={Add01Icon}
                    size={16}
                    strokeWidth={2}
                    className="mr-2"
                  />
                  Add Account
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {paginatedAccounts.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12">
                <HugeiconsIcon
                  icon={Search01Icon}
                  size={48}
                  strokeWidth={2}
                  className="text-muted-foreground mb-4"
                />
                <h3 className="text-lg font-semibold mb-2">
                  No matching accounts
                </h3>
                <p className="text-muted-foreground text-center">
                  No accounts match "{searchQuery}". Try a different search
                  term.
                </p>
              </div>
            ) : (
              <>
                <TooltipProvider>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Name</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>Currency</TableHead>
                        <TableHead>Side</TableHead>
                        <TableHead>Liquidity</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {paginatedAccounts.map(account => (
                        <TableRow
                          key={account._id}
                          className={account.isArchived ? 'opacity-50' : ''}
                        >
                          <TableCell className="font-medium">
                            <div className="flex items-center gap-2">
                              {account.name}
                              {account.isArchived && (
                                <Badge variant="secondary" className="text-xs">
                                  Archived
                                </Badge>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            {account.accountType?.label || 'Unknown'}
                          </TableCell>
                          <TableCell>{account.baseCurrency}</TableCell>
                          <TableCell>
                            <Badge
                              variant="secondary"
                              className={
                                account.side === 'Asset'
                                  ? 'bg-green-100 text-green-900 hover:bg-green-100 dark:bg-green-900/30 dark:text-green-400 dark:hover:bg-green-900/30'
                                  : 'bg-red-100 text-red-900 hover:bg-red-100 dark:bg-red-900/30 dark:text-red-400 dark:hover:bg-red-900/30'
                              }
                            >
                              {account.side}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Badge
                              variant="secondary"
                              className={
                                account.liquidity === 'Liquid'
                                  ? 'bg-blue-100 text-blue-900 hover:bg-blue-100 dark:bg-blue-900/30 dark:text-blue-400 dark:hover:bg-blue-900/30'
                                  : 'bg-amber-100 text-amber-900 hover:bg-amber-100 dark:bg-amber-900/30 dark:text-amber-400 dark:hover:bg-amber-900/30'
                              }
                            >
                              {account.liquidity}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleEdit(account._id)}
                              >
                                <HugeiconsIcon
                                  icon={PencilEdit01Icon}
                                  size={16}
                                  strokeWidth={2}
                                />
                              </Button>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleArchive(account._id)}
                                  >
                                    {account.isArchived ? (
                                      <HugeiconsIcon
                                        icon={UndoIcon}
                                        size={16}
                                        strokeWidth={2}
                                      />
                                    ) : (
                                      <HugeiconsIcon
                                        icon={Archive03Icon}
                                        size={16}
                                        strokeWidth={2}
                                      />
                                    )}
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>
                                  {account.isArchived
                                    ? 'Restore account'
                                    : 'Archive account'}
                                </TooltipContent>
                              </Tooltip>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() =>
                                      handleDeleteClick(account._id)
                                    }
                                  >
                                    <HugeiconsIcon
                                      icon={Delete01Icon}
                                      size={16}
                                      strokeWidth={2}
                                    />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>
                                  Delete account and all history
                                </TooltipContent>
                              </Tooltip>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TooltipProvider>

                {totalPages > 1 && (
                  <div className="mt-4">
                    <Pagination>
                      <PaginationContent>
                        <PaginationItem>
                          <PaginationPrevious
                            onClick={() =>
                              setCurrentPage(p => Math.max(1, p - 1))
                            }
                            className={
                              currentPageNumber === 1
                                ? 'pointer-events-none opacity-50'
                                : 'cursor-pointer'
                            }
                          />
                        </PaginationItem>

                        {Array.from({ length: totalPages }, (_, i) => i + 1)
                          .filter(page => {
                            // Show first page, last page, current page, and pages around current
                            return (
                              page === 1 ||
                              page === totalPages ||
                              Math.abs(page - currentPageNumber) <= 1
                            );
                          })
                          .map((page, index, array) => {
                            // Add ellipsis if there's a gap
                            const showEllipsisBefore =
                              index > 0 && page - array[index - 1] > 1;

                            return (
                              <React.Fragment key={page}>
                                {showEllipsisBefore && (
                                  <PaginationItem>
                                    <span className="px-4">...</span>
                                  </PaginationItem>
                                )}
                                <PaginationItem>
                                  <PaginationLink
                                    onClick={() => setCurrentPage(page)}
                                    isActive={page === currentPageNumber}
                                    className="cursor-pointer"
                                  >
                                    {page}
                                  </PaginationLink>
                                </PaginationItem>
                              </React.Fragment>
                            );
                          })}

                        <PaginationItem>
                          <PaginationNext
                            onClick={() =>
                              setCurrentPage(p => Math.min(totalPages, p + 1))
                            }
                            className={
                              currentPageNumber === totalPages
                                ? 'pointer-events-none opacity-50'
                                : 'cursor-pointer'
                            }
                          />
                        </PaginationItem>
                      </PaginationContent>
                    </Pagination>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      )}

      <AccountModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        balanceSheetId={id as BalanceSheetId}
        accountId={editingAccount}
      />

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Account?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete this
              account and <strong>all historical balance data</strong>{' '}
              associated with it.
              <br />
              <br />
              <strong>Consider archiving instead</strong> to preserve the
              history while hiding it from active use.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete Permanently
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export const Route = createFileRoute('/balance-sheet/$id/accounts')({
  component: AuthenticatedRoute(AccountsPage),
});
